import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DeleteDialog } from "@/components/ui/delete-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import VehicleSelect from "@/components/ui/vehicle-select";
import { errorHandler } from "@/lib/utils";
import {
  ReferencePointReturnRuleInput,
  referencePointReturnRuleService,
} from "@/services/api/referencePointReturnRules";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const WEEKDAYS = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda-feira" },
  { value: 2, label: "Terça-feira" },
  { value: 3, label: "Quarta-feira" },
  { value: 4, label: "Quinta-feira" },
  { value: 5, label: "Sexta-feira" },
  { value: 6, label: "Sábado" },
] as const;

const FULL_DAY_SECONDS = 86400;
const DEFAULT_START = "06:00";
const DEFAULT_END = "20:00";

type DayMode = "allowed" | "blocked" | "free";

const timeToSeconds = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};

const endTimeToSeconds = (time: string): number =>
  time === "00:00" ? FULL_DAY_SECONDS : timeToSeconds(time);

const secondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600) % 24;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const TIME_PATTERN = /^\d{2}:\d{2}$/;

const returnRuleFormSchema = z.object({
  active: z.boolean(),
  days: z.array(
    z
      .object({
        weekday: z.number(),
        mode: z.enum(["allowed", "blocked", "free"]),
        start: z.string(),
        end: z.string(),
      })
      .superRefine((day, ctx) => {
        if (day.mode !== "allowed") return;
        if (!TIME_PATTERN.test(day.start) || !TIME_PATTERN.test(day.end)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["end"],
            message: "Informe o horário de início e fim.",
          });
          return;
        }
        if (endTimeToSeconds(day.end) <= timeToSeconds(day.start)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["end"],
            message: "O fim precisa ser depois do início.",
          });
        }
      }),
  ),
  vehicles: z.array(z.number()),
});

type ReturnRuleFormValues = z.infer<typeof returnRuleFormSchema>;

const defaultDays = () =>
  WEEKDAYS.map((day) => ({
    weekday: day.value,
    mode: (day.value === 0 || day.value === 6
      ? "blocked"
      : "allowed") as DayMode,
    start: DEFAULT_START,
    end: DEFAULT_END,
  }));

interface ReturnRuleSectionProps {
  referencePointId: number;
  hasGeometry: boolean;
}

const ReturnRuleSection = ({
  referencePointId,
  hasGeometry,
}: ReturnRuleSectionProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ruleId, setRuleId] = useState<number | null>(null);

  const form = useForm<ReturnRuleFormValues>({
    resolver: zodResolver(returnRuleFormSchema),
    defaultValues: { active: true, days: defaultDays(), vehicles: [] },
  });

  useEffect(() => {
    let cancelled = false;

    referencePointReturnRuleService
      .show(referencePointId)
      .then((response) => {
        if (cancelled) return;
        const rule = response.data;
        setRuleId(rule?.id ?? null);

        if (rule) {
          const periodByWeekday = new Map(
            rule.allowed_periods.map((period) => [period.weekday, period]),
          );
          form.reset({
            active: rule.active,
            days: WEEKDAYS.map((day) => {
              const period = periodByWeekday.get(day.value);
              if (!period) {
                return {
                  weekday: day.value,
                  mode: "blocked" as DayMode,
                  start: DEFAULT_START,
                  end: DEFAULT_END,
                };
              }
              const isFullDay =
                period.start_seconds === 0 &&
                period.end_seconds === FULL_DAY_SECONDS;
              return {
                weekday: day.value,
                mode: (isFullDay ? "free" : "allowed") as DayMode,
                start: isFullDay
                  ? DEFAULT_START
                  : secondsToTime(period.start_seconds),
                end: isFullDay
                  ? DEFAULT_END
                  : secondsToTime(period.end_seconds),
              };
            }),
            vehicles: rule.vehicles,
          });
        }
      })
      .catch((err) => errorHandler(err, "Erro ao carregar alerta de retorno"))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [referencePointId, form]);

  const applyServerErrors = (err: unknown) => {
    if (!axios.isAxiosError(err) || err.response?.status !== 422) {
      errorHandler(err, "Erro ao salvar alerta de retorno");
      return;
    }

    const failures: Record<string, string[]> =
      err.response.data?.data?.failures ?? {};
    const failureKeys = Object.keys(failures);

    if (failureKeys.includes("reference_point_id")) {
      form.setError("root", {
        message:
          "Este ponto ainda não tem uma área desenhada no mapa. Desenhe o círculo ou polígono, salve o cadastro e tente novamente.",
      });
    }

    const dayKey = failureKeys.find((key) => key.startsWith("allowed_periods"));
    if (dayKey) {
      form.setError("days", { message: failures[dayKey][0] });
    }

    const vehicleKey = failureKeys.find((key) => key.startsWith("vehicles"));
    if (vehicleKey) {
      form.setError("vehicles", { message: failures[vehicleKey][0] });
    }

    if (failureKeys.length === 0) {
      errorHandler(err, "Erro ao salvar alerta de retorno");
    }
  };

  const onSave = async (data: ReturnRuleFormValues) => {
    setSaving(true);
    try {
      const payload: ReferencePointReturnRuleInput = {
        active: data.active,
        allowed_periods: data.days
          .filter((day) => day.mode !== "blocked")
          .map((day) =>
            day.mode === "free"
              ? {
                  weekday: day.weekday,
                  start_seconds: 0,
                  end_seconds: FULL_DAY_SECONDS,
                }
              : {
                  weekday: day.weekday,
                  start_seconds: timeToSeconds(day.start),
                  end_seconds: endTimeToSeconds(day.end),
                },
          ),
        vehicles: data.vehicles,
      };

      const response = await referencePointReturnRuleService.update(
        referencePointId,
        payload,
      );
      setRuleId(response.data.id);
      toast.success("Alerta de retorno salvo com sucesso!");
    } catch (err) {
      applyServerErrors(err);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    try {
      await referencePointReturnRuleService.destroy(referencePointId);
      setRuleId(null);
      form.reset({ active: true, days: defaultDays(), vehicles: [] });
      toast.success("Alerta de retorno removido com sucesso!");
    } catch (err) {
      errorHandler(err, "Erro ao remover alerta de retorno");
    }
  };

  if (!hasGeometry) {
    return (
      <Card className="mx-auto">
        <CardHeader>
          <CardTitle>Alerta de retorno</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Desenhe a área deste ponto no mapa acima (círculo ou polígono) e
            salve o cadastro antes de configurar o alerta de retorno.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="mx-auto">
        <CardHeader>
          <CardTitle>Alerta de retorno</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-10 w-full bg-muted" />
          <Skeleton className="h-10 w-full bg-muted" />
          <Skeleton className="h-10 w-full bg-muted" />
        </CardContent>
      </Card>
    );
  }

  // falhas 422 do servidor entram por form.setError("days", ...)
  const daysErrorMessage = form.formState.errors.days?.message;

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>Alerta de retorno</CardTitle>
        <CardDescription>
          Fora do horário permitido, o veículo precisa estar dentro deste ponto.
          Avisamos quando ele não retornar ou quando sair.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <div className="space-y-6">
            {form.formState.errors.root?.message && (
              <p className="text-sm text-danger">
                {form.formState.errors.root.message}
              </p>
            )}

            <div className="flex flex-col gap-6 xl:grid xl:grid-cols-3">
              <div className="w-full max-w-xl space-y-6 xl:col-span-1 xl:max-w-none">
                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium">Alerta ativo</p>
                        <p className="text-sm text-muted-foreground">
                          Desative para pausar o alerta sem perder a
                          configuração.
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vehicles"
                  render={({ field }) => (
                    <FormItem>
                      <p className="text-sm font-medium">
                        Veículos monitorados
                      </p>
                      <FormControl>
                        <VehicleSelect
                          value={field.value}
                          onChange={(value) =>
                            field.onChange(
                              Array.isArray(value) ? value : [value],
                            )
                          }
                          multiple
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2 xl:col-span-2">
                <p className="text-sm font-medium">
                  Horário permitido fora do ponto
                </p>
                {WEEKDAYS.map((day) => {
                  const mode = form.watch(`days.${day.value}.mode`);
                  const endError =
                    form.formState.errors.days?.[day.value]?.end?.message;
                  return (
                    <div key={day.value} className="space-y-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="w-32 text-sm">{day.label}</span>
                        <FormField
                          control={form.control}
                          name={`days.${day.value}.mode`}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="w-44">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="allowed">
                                  Pode sair
                                </SelectItem>
                                <SelectItem value="blocked">
                                  Não pode sair
                                </SelectItem>
                                <SelectItem value="free">
                                  Livre o dia todo
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {mode === "allowed" && (
                          <>
                            <span className="text-sm">das</span>
                            <FormField
                              control={form.control}
                              name={`days.${day.value}.start`}
                              render={({ field }) => (
                                <div className="w-28">
                                  <Input type="time" {...field} />
                                </div>
                              )}
                            />
                            <span className="text-sm">às</span>
                            <FormField
                              control={form.control}
                              name={`days.${day.value}.end`}
                              render={({ field }) => (
                                <div className="w-28">
                                  <Input type="time" {...field} />
                                </div>
                              )}
                            />
                          </>
                        )}
                      </div>
                      {mode === "allowed" && endError && (
                        <p className="text-sm font-medium text-destructive">
                          {endError}
                        </p>
                      )}
                    </div>
                  );
                })}
                {daysErrorMessage && (
                  <p className="text-sm font-medium text-destructive">
                    {daysErrorMessage}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                disabled={saving}
                onClick={form.handleSubmit(onSave)}
                className="flex items-center space-x-2 bg-primary-500 dark:bg-primary-400 text-white dark:hover:opacity-90"
                variant="outline"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? "Salvando..." : "Salvar alerta"}</span>
              </Button>
              {ruleId && (
                <DeleteDialog
                  row={{ id: ruleId }}
                  onDelete={onDelete}
                  label="Remover alerta"
                  size="default"
                  message="Tem certeza que deseja remover o alerta de retorno deste ponto?"
                />
              )}
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ReturnRuleSection;
